import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getDownloadURL, ref } from "firebase/storage";
import { db, storage } from '@/lib/firebase';

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { error } from 'pdf-lib';

interface FormState {
  name: string;
  email: string;
  phone: string;
  content: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  content?: string;
}

const DataPrivacyRequest: React.FC = () => {
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [formState, setFormState] = useState<FormState>({
      name: "",
      email: "",
      phone: "",
      content: "",
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitted, setSubmitted] = useState(false);

    const [customerInfo, setCustomerInfo] = useState<string>(null);
    const [customerLogoURL, setCustomerLogoURL] = useState<string | null>(null);
    useEffect(() => {
      document.title = 'Privacy Request | de.iterate.com';
      const params = new URLSearchParams(window.location.search);
      const id = params.get("customerId");

      if (!id) return; setCustomerId(id);

      const checkPrivacyModuleEnabled = async () => {
        const docRef = doc(db, 'customers', id, 'companySettings', 'complianceModules');
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) return null;

        const docData = docSnap.data();

        console.log('d', docData);
      };

      const fetchCustomerLogo = async () => {
        try {
          const apiUrl = import.meta.env.VITE_cloudFunctionURL + '/deiterateInternalAPI/tenancyInfo/getPublicTenancyData';
          const customerPublicInfo = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              apiKey: "",
              companyID: id,
              method: "getCompanyLogo"
            }),
          });

          if (!customerPublicInfo.ok) console.error(customerPublicInfo.statusText);

          const data = await customerPublicInfo.json();

          const imgRef = ref(storage, data.companyLogo);
          const url = await getDownloadURL(imgRef);

          return url || null;
        } catch (error: any) {
          console.log(error);
          return null;
        }
      };
      const fetchCustomerInfo = async () => {
        const docRef = doc(db,"customers", id, "publicData", "companyData");
        const docSnap = await getDoc(docRef);

        if (!docSnap) return null;
        if (!docSnap.exists()) return null;

        const docData = docSnap.data();

        return docData.companyName || null;
      };

      checkPrivacyModuleEnabled();
      fetchCustomerLogo().then(setCustomerLogoURL);
      fetchCustomerInfo().then(setCustomerInfo);

    }, []);

    const validate = (state: FormState): FormErrors => {
      const nextErrors: FormErrors = {};

      // Name required
      if (!state.name.trim()) {
        nextErrors.name = "Name is required.";
      }

      // Email required + format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!state.email.trim()) {
        nextErrors.email = "Email is required.";
      } else if (!emailRegex.test(state.email)) {
        nextErrors.email = "Enter a valid email address.";
      }

      // Phone optional but must be numbers or '+' if provided
      const phoneRegex = /^[+\d]*$/;
      if (state.phone && !phoneRegex.test(state.phone)) {
        nextErrors.phone = "Phone can only contain digits or '+'.";
      }

      // Content required
      if (!state.content.trim()) {
        nextErrors.content = "Please enter a message.";
      }

      return nextErrors;
    };

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const { id, value } = e.target;

      // Allow only digits and '+' for phone
      if (id === "phone") {
        const sanitized = value.replace(/[^+\d]/g, "");
        setFormState((prev) => ({ ...prev, [id]: sanitized }));
        // live-validate phone
        setErrors((prev) => ({ ...prev, phone: sanitized && !/^[+\d]*$/.test(sanitized) ? "Phone can only contain digits or '+'." : undefined }));
        return;
      }

      setFormState((prev) => ({ ...prev, [id]: value }));

      // live-validate specific fields
      if (id === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setErrors((prev) => ({
          ...prev,
          email: !value.trim() ? "Email is required." : emailRegex.test(value) ? undefined : "Enter a valid email address.",
        }));
      }

      if (id === "name") {
        setErrors((prev) => ({ ...prev, name: value.trim() ? undefined : "Name is required." }));
      }

      if (id === "content") {
        setErrors((prev) => ({ ...prev, content: value.trim() ? undefined : "Please enter a message." }));
      }
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      const nextErrors = validate(formState);
      setErrors(nextErrors);


      if (Object.keys(nextErrors).length > 0) {
        return; // do not submit
      }

      const apiUrl = import.meta.env.VITE_THALASSA_API + "/public/v1/prt/";
      // const apiUrl = "http://localhost:3000/public/v1/prt/";

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customer_id: customerId,
            name: formState.name,
            email: formState.email,
            phone: formState.phone,
            message: formState.content,
          }),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        console.log("Response:", data);

        setSubmitted(true);
      } catch (err) {
        console.error("Error submitting request:", err);
      }
    };

    return (
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <header className="bg-[#585555] flex flex-col items-center justify-center px-6 py-6 shadow-sm space-y-2">
          <div className="max-w-xs max-h-xs bg-gray-300 overflow-hidden flex items-center justify-center">
            {customerLogoURL && (
              <img
                src={customerLogoURL}
                alt="Customer Logo"
                className="w-full h-full object-cover"
              />
            )}
          </div>
          {customerInfo && (
            <h1 className="text-2xl font-semibold text-white tracking-tight">{customerInfo}</h1>
          )}
        </header>

        {/* Body */}
        <main className="flex-grow flex justify-center items-center p-6 bg-gray-50">
          <Card className="w-full max-w-lg shadow-md">
            <CardContent className="p-6 space-y-4">
              {submitted ? (
                <div className="text-center text-green-600 font-medium">
                  Your request has been submitted successfully.
                </div>
              ) : (
                <>
                  <h1 className="text-2xl text-center font-semibold tracking-tight">Data Privacy Request</h1>
                  <form className="space-y-4" onSubmit={handleSubmit} noValidate>
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="name"
                        value={formState.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="mt-1"
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? "name-error" : undefined}
                      />
                      {errors.name && (
                        <p id="name-error" className="mt-1 text-sm text-red-600">{errors.name}</p>
                      )}
                    </div>

                    {/* Email & Phone */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="email"
                          type="email"
                          value={formState.email}
                          onChange={handleChange}
                          placeholder="you@example.com"
                          className="mt-1"
                          aria-invalid={Boolean(errors.email)}
                          aria-describedby={errors.email ? "email-error" : undefined}
                        />
                        {errors.email && (
                          <p id="email-error" className="mt-1 text-sm text-red-600">{errors.email}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone</label>
                        <Input
                          id="phone"
                          inputMode="tel"
                          value={formState.phone}
                          onChange={handleChange}
                          className="mt-1"
                          aria-invalid={Boolean(errors.phone)}
                          aria-describedby={errors.phone ? "phone-error" : undefined}
                        />
                        {errors.phone && (
                          <p id="phone-error" className="mt-1 text-sm text-red-600">{errors.phone}</p>
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div>
                      <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                        Content <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        id="content"
                        value={formState.content}
                        onChange={handleChange}
                        placeholder="Write your message..."
                        className="mt-1"
                        rows={4}
                        aria-invalid={Boolean(errors.content)}
                        aria-describedby={errors.content ? "content-error" : undefined}
                      />
                      {errors.content && (
                        <p id="content-error" className="mt-1 text-sm text-red-600">{errors.content}</p>
                      )}
                    </div>

                    {/* Submit */}
                    <Button type="submit" className="w-full">Submit</Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }
;

export default DataPrivacyRequest;
